import { useVideoVersionHistoryService } from '@src/hooks/useVideoVersionHistoryService';
import { useStore } from '@src/stores-v2/StoreContext';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { VideoVersion } from '@src/types.ts/story';

/** The Clip History preview the list drives (`ClipVersionPreviewStore`). */
export interface ClipVersionPreviewView {
  previewingVersion: VideoVersion | null;
  error: string | null;
  select(version: VideoVersion): Promise<void>;
  exit(): void;
}

export interface ClipVersionHistoryInput {
  /** Whether the History tab is showing — nothing is fetched while it is not. */
  isOpen: boolean;
  /** The active variant's video record; undefined until its first save. */
  videoId?: string;
  /** The adapter's last persisted save — a change moves the head, so refetch. */
  lastSavedAt: Date | null;
  preview: ClipVersionPreviewView;
}

export interface ClipVersionHistoryView {
  versions?: VideoVersion[];
  isLoading: boolean;
  /** The version the editor shows: the one previewed, else the head. */
  currentVersionId?: string;
  page: number;
  totalPages?: number;
  error: string | null;
  setPage: (page: number) => void;
  select: (versionId: string) => void;
}

/**
 * Host-owned controller for the clip preview's History tab: lists the active
 * variant's saved versions and routes a pick to the preview. Picking the head
 * leaves the preview.
 */
export function useClipVersionHistory({
  isOpen,
  videoId,
  lastSavedAt,
  preview,
}: ClipVersionHistoryInput): ClipVersionHistoryView {
  const service = useVideoVersionHistoryService();
  const { userIdentityStore } = useStore();

  const [versions, setVersions] = useState<VideoVersion[]>();
  const [page, setPageState] = useState(0);
  const [totalPages, setTotalPages] = useState<number>();
  const [headVersionId, setHeadVersionId] = useState<string>();
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Drops responses that a newer request (page change, new save) superseded.
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (targetPage: number) => {
      if (!videoId) {
        // Not saved yet: nothing to list, and the previous variant's rows
        // must not linger — nor land late.
        requestIdRef.current++;
        setIsFetching(false);
        setVersions([]);
        setTotalPages(0);
        return;
      }
      const requestId = ++requestIdRef.current;
      setIsFetching(true);
      setError(null);
      const result = await service.listVersions({
        videoId,
        page: targetPage,
        isExternalUser: userIdentityStore.isExternalUser(),
      });
      if (requestId !== requestIdRef.current) return;
      setIsFetching(false);
      if (!result.ok) {
        setError('Could not load version history.');
        return;
      }
      setVersions(result.value.versions);
      setPageState(result.value.page);
      setTotalPages(result.value.totalPages);
      // Only the first page holds the head.
      if (targetPage === 0) setHeadVersionId(result.value.headVersionId);
    },
    [service, userIdentityStore, videoId],
  );

  // Open, variant switch, or a save that landed: back to the newest page.
  useEffect(() => {
    if (!isOpen) return;
    void fetchPage(0);
  }, [isOpen, fetchPage, lastSavedAt]);

  const select = useCallback(
    (versionId: string) => {
      if (versionId === headVersionId) {
        preview.exit();
        return;
      }
      const version = versions?.find(v => v.versionId === versionId);
      if (version) void preview.select(version);
    },
    [versions, headVersionId, preview],
  );

  return {
    versions,
    isLoading: isFetching,
    currentVersionId: preview.previewingVersion?.versionId ?? headVersionId,
    page,
    totalPages,
    error: error ?? preview.error,
    setPage: p => void fetchPage(p),
    select,
  };
}
